import conversationClient from 'api/conversation-client';
import { routesNO, RouteList, routesEN } from 'api/routes';
import { Layout } from 'components/layout';
import { Loading } from 'components/loading';
import { SEO } from 'components/seo';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { THEMES_CATEGORY } from 'services/conversation-service';
import { getWindowScrollPosition } from 'services/get-scroll-pos';
import {
    scrollPosSelectorHomePage,
    setScrollPosHomePage,
    languageSelector,
} from 'store-modules/app-settings';
import { buildFlatTreeFromArray, FlatTree } from 'utils/tree-utils';
import {
    Conversation,
    ConversationCategory,
    ConversationWithCategory,
    deckParentPages,
    Question,
} from '_types';
import ConversationCard from '../../components/conversation-card/conversation-card';
import ThemeCard from '../../components/conversation-card/theme-card';
import { ConversationsRow } from './conversations-row';
import styles from './home-page.module.scss';
import {
    getMainCategories,
    groupConversationsByCategory,
} from './_services/home-page.service';
import { Language } from '_types/language';
import {
    returnCopyForLanguage,
    returnRoutesForLanguage,
} from 'services/language-service';
import { useWindowSize } from 'hooks';
import Decorator from 'components/conversation-card/decorator';
import { useNavigate } from 'react-router-dom';
import useConversationSelector from 'hooks/use-conversation-selector';
import classNames from 'classnames';
import { getUAInfo } from 'services/user-agent.service';
import CategorySelector from './category-selector/category-selector';

interface ViewCopy {
    pageTitle: string;
    randomCardTitle: string;
    themesTitle: string;
    conversationsLabel: string;
    randomSlug: string;
    newConversationsTitle: string;
    featuredConversationsTitle: string;
    suggestedConversationsTitle: string;
    browseText: string;
}

const nbCopy: ViewCopy = {
    pageTitle: 'Hjem',
    randomCardTitle: 'Velg et tilfeldig kort',
    themesTitle: 'Aktuelle tema',
    conversationsLabel: 'Relasjon samtale',
    randomSlug: 'kort',
    newConversationsTitle: 'Nyeste samtaler',
    featuredConversationsTitle: 'Utvalgte samtaler',
    suggestedConversationsTitle: 'Forslag',
    browseText: 'Velg en kategori',
};

const enCopy: ViewCopy = {
    pageTitle: '',
    randomCardTitle: 'Pick a random card',
    themesTitle: 'Timely topics',
    conversationsLabel: 'Relasjon samtale',
    randomSlug: 'card',
    newConversationsTitle: 'New conversations',
    featuredConversationsTitle: 'Featured conversations',
    suggestedConversationsTitle: 'Suggestions',
    browseText: 'Choose a category',
};

const copies: { name: Language; copy: ViewCopy }[] = [
    {
        name: 'nb',
        copy: nbCopy,
    },
    {
        name: 'en',
        copy: enCopy,
    },
];

const routeObj: { name: Language; routes: RouteList }[] = [
    {
        name: 'nb',
        routes: routesNO,
    },
    {
        name: 'en',
        routes: routesEN,
    },
];

type Content = {
    themes: ConversationWithCategory[];
    conversations: ConversationWithCategory[];
    randomQuestions: Question[];
    myConversations: ConversationWithCategory[];
    newlyAddedConversations: ConversationWithCategory[];
    featuredConversations: ConversationWithCategory[];
    suggestedConversations: ConversationWithCategory[];
};

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const language: Language = useSelector(languageSelector);
    const {
        pageTitle,
        themesTitle,
        conversationsLabel,
        newConversationsTitle,
        featuredConversationsTitle,
        suggestedConversationsTitle,
        browseText,
    } = returnCopyForLanguage(language, copies);
    const routes = returnRoutesForLanguage(language, routeObj);
    const scrollPosition = useSelector(scrollPosSelectorHomePage);
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const { view } = useWindowSize(window);
    const isMobile = view === 'mobile';
    const { isAndroid } = getUAInfo(window.navigator);

    const [content, setContent] = useState<Content>({
        conversations: [],
        randomQuestions: [],
        themes: [],
        myConversations: [],
        newlyAddedConversations: [],
        featuredConversations: [],
        suggestedConversations: [],
    });

    const {
        conversations,
        themes,
        newlyAddedConversations,
        featuredConversations,
        suggestedConversations,
    } = content;

    const { selectConversation } = useConversationSelector();

    const headerProps = {
        showLogoLink: true,
    };

    useEffect(() => {
        Promise.all([
            conversationClient.getThemes(),
            conversationClient.getConversations(),
            conversationClient.getRandomQuestions(),
            conversationClient.getMyFavouriteConversations(),
            conversationClient.getNewlyAddedConversations(),
            conversationClient.getFeaturedConversations(),
            conversationClient.getSuggestedConversations(),
        ])
            .then((response) =>
                setContent({
                    themes: response[0],
                    conversations: response[1],
                    randomQuestions: response[2],
                    myConversations: response[3],
                    newlyAddedConversations: response[4],
                    featuredConversations: response[5],
                    suggestedConversations: response[6],
                })
            )
            .catch((err) => {
                console.error(err);
                navigate(routes.error);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch, routes, isMobile]);

    useEffect(() => {
        if (scrollPosition)
            window.scrollTo({
                top: scrollPosition.y,
                left: scrollPosition.x,
            });
    }, [content, scrollPosition]);

    if (isLoading) {
        return <Loading language={language} />;
    }

    const onConversationSelect = (conversation: Conversation) => {
        dispatch(setScrollPosHomePage(getWindowScrollPosition(window)));
        selectConversation(conversation, language, deckParentPages.premiumHome);
    };

    const onClickShowAll = (category: ConversationCategory) => {
        dispatch(setScrollPosHomePage(getWindowScrollPosition(window)));
        navigate(`browse/${category.slug}`);
    };

    const themesTree = buildFlatTreeFromArray(themes);
    const filteredThemes = themes.filter(
        ({ id }) => themesTree.nodesById[id].childrenIds.length === 0
    );

    const conversationTree: FlatTree<ConversationWithCategory> =
        buildFlatTreeFromArray(conversations);
    const mainCategories = getMainCategories(conversationTree);
    const byCategoryId = groupConversationsByCategory(conversationTree);

    const createThemeCard = (
        category: ConversationCategory,
        conversation: Conversation
    ): ReactElement => {
        return (
            <ConversationCard
                conversation={conversation}
                onClick={() => onConversationSelect(conversation)}
                category={category}
            />
        );
    };

    const isThemeCard = (taxonomy: string) => {
        switch (taxonomy.toLocaleLowerCase()) {
            case 'topics':
                return true;
            case 'tema':
                return true;
            default:
                return false;
        }
    };
    const isOrganizationCard = (taxonomy: string) => {
        switch (taxonomy.toLocaleLowerCase()) {
            case 'procustomer':
                return true;
            case 'proffkunde':
                return true;
            default:
                return false;
        }
    };

    const conversationCardStyle = (convo: ConversationWithCategory) => {
        let classNamesToReturn = '';
        const conversationIndex = newlyAddedConversations?.indexOf(convo);
        let isLeftCard = conversationIndex && conversationIndex % 2 === 0;

        if (isLeftCard === 0) {
            isLeftCard = true;
        }

        classNamesToReturn = classNames(
            styles.FavouriteConversationCard,
            isOrganizationCard(convo.taxonomy) && styles.OrganizationCard,
            isAndroid && styles.AndroidCard,
            isThemeCard(convo.taxonomy) && styles.FavouriteThemeCard,
            isLeftCard && isMobile && styles.LeftCard,
            !isLeftCard && isMobile && styles.RightCard,
            convo.created_by_id &&
                convo.color_option === 1 &&
                'userCreatedConversation1',
            convo.created_by_id &&
                convo.color_option === 2 &&
                'userCreatedConversation2',
            convo.created_by_id &&
                convo.color_option === 3 &&
                'userCreatedConversation3'
        );

        return classNamesToReturn;
    };

    const decorator = new Decorator();
    const createConversationCard = (
        category: ConversationCategory,
        conversation: ConversationWithCategory,
        wideConversationCard?: boolean
    ): ReactElement => {
        return isThemeCard(conversation.taxonomy) ||
            isOrganizationCard(conversation.taxonomy) ? (
            <ThemeCard
                key={conversation.id}
                conversation={conversation}
                className={conversationCardStyle(conversation)}
                onClick={(): void => onConversationSelect(conversation)}
            />
        ) : (
            <ConversationCard
                conversation={conversation}
                onClick={() => onConversationSelect(conversation)}
                category={category}
                decorator={decorator}
                className={conversationCardStyle(conversation)}
                wideConversationCard={wideConversationCard}
            />
        );
    };

    return (
        <Layout
            routes={routes}
            language={language}
            pageClassName={styles.homePage}
            headerProps={headerProps}
        >
            <SEO title={pageTitle} />

            <section className={styles.topSection}>
                {/* <div className={styles.infoText}>
                    The right question has the power to change everything.
                </div> */}
                <div className={styles.subInfoText}>
                    <span>Pick a question package and</span>
                    {isMobile && <br />}
                    <span> start the conversation</span>
                </div>

                {/* <Button
                    className={styles.randomCardButton}
                    fullWidth
                    onClick={selectRandomQuestion}
                    appearance='cta'
                    size={'large'}
                >
                    {randomCardTitle}
                </Button> */}
            </section>

            {featuredConversations && featuredConversations.length > 0 && (
                <section
                    aria-label={featuredConversationsTitle}
                    className={styles.featuredConversations}
                >
                    <ConversationsRow
                        key={-9090}
                        slidesId={-9090}
                        className={styles.conversationRow}
                        category={null}
                        title={''}
                        conversations={featuredConversations}
                        createCard={(category, conversation) =>
                            createConversationCard(category, conversation, true)
                        }
                        onClickShowAll={() =>
                            onClickShowAll({
                                slug:
                                    language === 'nb' ? 'featured' : 'featured',
                                name:
                                    language === 'nb' ? 'featured' : 'featured',
                            })
                        }
                        showAllComponent={ConversationCard}
                        wideConversationCard={true}
                    />
                </section>
            )}

            {suggestedConversations && suggestedConversations.length > 0 && (
                <section
                    aria-label={suggestedConversationsTitle}
                    className={styles.suggestedConversations}
                >
                    <ConversationsRow
                        key={-9091}
                        slidesId={-9091}
                        className={styles.conversationRow}
                        category={null}
                        title={suggestedConversationsTitle}
                        conversations={suggestedConversations}
                        createCard={(category, conversation) =>
                            createConversationCard(category, conversation)
                        }
                        onClickShowAll={() =>
                            onClickShowAll({
                                slug:
                                    language === 'nb'
                                        ? 'suggested'
                                        : 'suggested',
                                name:
                                    language === 'nb'
                                        ? 'suggested'
                                        : 'suggested',
                            })
                        }
                        showAllComponent={ConversationCard}
                    />
                </section>
            )}

            <div className={styles.browseTextOuterContainer}>
                <div className={styles.browseTextContainer}>
                    <div className={styles.browseText}>{browseText}</div>
                </div>
            </div>

            <CategorySelector categories={mainCategories} />

            {newlyAddedConversations && newlyAddedConversations.length > 5 && (
                <section aria-label={newConversationsTitle}>
                    <ConversationsRow
                        key={-9090}
                        slidesId={-9090}
                        className={classNames(
                            styles.conversationRow,
                            styles.newConversations
                        )}
                        category={null}
                        title={newConversationsTitle}
                        conversations={newlyAddedConversations}
                        createCard={createConversationCard}
                        onClickShowAll={() =>
                            onClickShowAll({
                                slug:
                                    language === 'nb'
                                        ? 'nyeste_samtaler'
                                        : 'new_conversations',
                                name:
                                    language === 'nb'
                                        ? 'Nyeste samtaler'
                                        : 'New conversations ',
                            })
                        }
                        showAllComponent={ConversationCard}
                    />
                </section>
            )}

            <section aria-label={themesTitle}>
                {themes.length > 0 && (
                    <ConversationsRow
                        slidesId={-99999}
                        className={styles.timelyTopics}
                        category={THEMES_CATEGORY}
                        title={themesTitle}
                        conversations={filteredThemes}
                        createCard={createThemeCard}
                        onClickShowAll={() => onClickShowAll(THEMES_CATEGORY)}
                        showAllComponent={ThemeCard}
                    />
                )}
            </section>
            <section aria-label={conversationsLabel}>
                {mainCategories.map((category) => {
                    const { id, name, title, slug } = category;
                    const conversations = byCategoryId[id];

                    if (conversations.length === 0) {
                        return null;
                    }

                    return (
                        <ConversationsRow
                            key={id}
                            slidesId={id}
                            className={styles.conversationRow}
                            category={{ slug, name }}
                            title={title || name}
                            conversations={conversations}
                            createCard={createConversationCard}
                            onClickShowAll={() =>
                                onClickShowAll({ slug, name })
                            }
                            showAllComponent={ConversationCard}
                        />
                    );
                })}
            </section>
        </Layout>
    );
};

export default HomePage;
